import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  PhoneOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Typography,
  notification,
  Modal,
  Input,
} from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import moment from "moment";
import ajaxService from "../../../services/ajax-service";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography;

const ContactUs = () => {
  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 10, total: 0 });
  const [type, setType] = useState(0);
  const [filter, setFilter] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debounceTimer = useRef(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 70,
      fixed: 'left',
      render: (text, record, index) => (
        <Text style={{ fontSize: '0.85rem' }}>{(dataSource.current_page - 1) * dataSource.per_page + index + 1}</Text>
      ),
    },
    {
      title: "Name",
      dataIndex: "user",
      key: "name",
      width: 150,
      ellipsis: true,
      render: (user) => <Text style={{ fontSize: '0.85rem' }}>{user?.name || 'N/A'}</Text>,
    },
    {
      title: "Email",
      dataIndex: "user",
      key: "email",
      width: 200,
      ellipsis: true,
      render: (user) => <Text style={{ fontSize: '0.85rem' }}>{user?.email || 'N/A'}</Text>,
    },
    {
      title: "Phone",
      dataIndex: "user",
      key: "phone",
      width: 150,
      ellipsis: true,
      render: (user) => {
        if (!user || !user.phone) return <Text style={{ fontSize: '0.85rem' }}>N/A</Text>;
        const code = user.country_code || user.country_code_phone || user.country?.country_code || '';
        return <Text style={{ fontSize: '0.85rem' }}>{code ? `+${code} ${user.phone}` : user.phone}</Text>;
      },
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: 120,
      ellipsis: true,
      render: (purpose) => <Text style={{ fontSize: '0.85rem' }}>{purpose}</Text>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 150,
      ellipsis: true,
      render: (subject) => <Text style={{ fontSize: '0.85rem' }}>{subject}</Text>,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 250,
      ellipsis: true,
      render: (message) => <Text style={{ fontSize: '0.85rem' }}>{message}</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      render: (created_at) => <Text style={{ fontSize: '0.85rem' }}>{new Date(created_at).toLocaleDateString()}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: 'right',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--primary-purple)',
              border: '1px solid var(--primary-purple)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<EyeOutlined style={{ fontSize: '12px' }} />}
            onClick={() => handleViewClick(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--error-red)',
              border: '1px solid var(--error-red)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
            onClick={() => handleDeleteClick(record.id)}
          />
        </div>
      ),
    },
  ];

  const handleViewClick = (record) => {
    setSelectedContact(record);
    setViewModalVisible(true);
  };

  const handleDeleteClick = (id) => {
    setContactToDelete(id);
    setDeleteModalVisible(true);
  };

  const formatPhone = (user) => {
    if (!user?.phone) return 'N/A';
    const code = user.country_code || user.country_code_phone || user.country?.country_code || '';
    return code ? `+${code} ${user.phone}` : user.phone;
  };

  const getPurposeBadgeClass = (purpose) => {
    const value = purpose?.toLowerCase();
    if (value === 'complaint') return 'contact-view-modal__badge--complaint';
    if (value === 'feedback') return 'contact-view-modal__badge--feedback';
    return 'contact-view-modal__badge--default';
  };

  const renderViewModal = () => {
    if (!selectedContact) return null;

    const { user, purpose, subject, message, created_at } = selectedContact;
    const userName = user?.name || 'Unknown User';
    const userInitial = userName.charAt(0).toUpperCase();

    return (
      <Modal
        className="modal-overlay contact-view-modal"
        width={calculateModalWidth('560px')}
        open={viewModalVisible}
        maskClosable
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        centered
      >
        <div className="contact-view-modal__shell">
          <div className="contact-view-modal__header">
            <div className="contact-view-modal__avatar" aria-hidden="true">
              {userInitial}
            </div>
            <div className="contact-view-modal__header-main">
              <div className="contact-view-modal__title-row">
                <p className="contact-view-modal__name">{userName}</p>
                {purpose && (
                  <span className={`contact-view-modal__badge ${getPurposeBadgeClass(purpose)}`}>
                    {purpose}
                  </span>
                )}
              </div>
              <p className="contact-view-modal__email">{user?.email || 'No email provided'}</p>
            </div>
          </div>

          <div className="ud-info-grid">
            <div className="ud-info-tile">
              <div className="ud-info-label">
                <PhoneOutlined /> Phone
              </div>
              <div className="ud-info-value">{formatPhone(user)}</div>
            </div>
            <div className="ud-info-tile">
              <div className="ud-info-label">
                <CalendarOutlined /> Submitted
              </div>
              <div className="ud-info-value">
                {created_at ? moment(created_at).format('DD MMM YYYY, hh:mm A') : 'N/A'}
              </div>
            </div>
            <div className="ud-info-tile ud-span-2">
              <div className="ud-info-label">Subject</div>
              <div className="ud-info-value contact-view-modal__subject-text">
                {subject || 'N/A'}
              </div>
            </div>
          </div>

          <div className="contact-view-modal__message-section">
            <div className="contact-view-modal__message-label">Message</div>
            <div className="contact-view-modal__message-box">
              {message || 'No message provided.'}
            </div>
          </div>

          <div className="contact-view-modal__footer">
            <Button
              className="btn-secondary"
              onClick={() => setViewModalVisible(false)}
              style={{ minWidth: '110px', height: '38px' }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const handleConfirmDelete = async () => {
    if (contactToDelete) {
      try {
        const response = await ajaxService.delete('/contact-us/', contactToDelete);
        if (response.success) {
          notification.open({
            message: `Contact deleted successfully!`,
            icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
          });
          fetchData(1);
        } else {
          notification.open({
            message: "Unable to delete contact.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
          });
        }
      } catch (error) {
        console.error('Delete error:', error);
        notification.open({
          message: "Unable to delete contact.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
    setDeleteModalVisible(false);
    setContactToDelete(null);
  };

  const handleSearch = async ({ name }) => {
    const query = name ? name.trim() : '';
    const response = await ajaxService.get(`/contact-us?type=${type}&search=${query}`);
    const { success, data } = response;
    console.log('response',response);
    if (success) {
      if (data.data.length == 0 && query) {
        setFilter('');
        notification.open({ message: `${query} is not found!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
      } else {
        setFilter(query);
      }
      setDataSource(data);
    }
  };

  // Debounced search handler
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchValue !== undefined) {
      debounceTimer.current = setTimeout(() => {
        handleSearch({ name: searchValue });
      }, 500);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue]);

  // Update search when type changes
  useEffect(() => {
    if (searchValue !== undefined) {
      handleSearch({ name: searchValue });
    }
  }, [type]);

  const handleChangePage = async (page) => {
    const response = await ajaxService.get(`/contact-us?type=${type}&search=${filter}&page=${page}`);
    if (response.success) {
      setDataSource(response.data);
    }
  };

  const fetchData = async (page = 1) => {
    const response = await ajaxService.get(`/contact-us?type=${type}&search=${filter}&page=${page}`);
    if (response.success) {
      setDataSource(response.data);
    }
  };

  const onClick = async (newType) => {
    setFilter('');
    setSearchValue('');
    setType(newType);
    const response = await ajaxService.get(`/contact-us?type=${newType}&search=`);
    if (response.success) {
      setDataSource(response.data);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  // Calculate table scroll width using standardized utility
  // Table columns: SNo(70) + Name(150) + Email(200) + Phone(150) + Purpose(120) + Subject(150) + Message(250) + Created(130) + Actions(120) = 1340px
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 1000, 1400, 40);
  }, [sidebarCollapsed]);

  const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 1000, 1400, 40));

  // Recalculate on window resize and sidebar toggle
  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth());
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">Contact Us Management</h1>
        <p className="module-subtitle">Manage and monitor user feedback and complaints</p>
      </div>

      {/* Filters and Search */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search by subject, purpose or name..."
              prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => {
                setSearchValue(e.target.value);
                handleSearch({ name: e.target.value });
              }}
              className="input-field"
              allowClear
            />
          </div>
          <div className="filter-actions">
            <Button
              className={type === 1 ? "btn-secondary-active" : "btn-secondary"}
              size="small"
              onClick={() => onClick(1)}
            >
              Feedback
            </Button>
            <Button
              className={type === 0 ? "btn-secondary-active" : "btn-secondary"}
              size="small"
              onClick={() => onClick(0)}
            >
              Complaint
            </Button>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Table
        columns={columns}
        dataSource={dataSource.data || []}
          className="data-table"
        rowKey="id"
        pagination={{
          onChange: handleChangePage,
          current: dataSource.current_page,
          pageSize: dataSource.per_page,
          total: dataSource.total,
            className: "data-table-pagination",
          showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts`,
            size: 'small'
        }}
          scroll={{ x: tableWidth }}
          size="small"
          bordered={false}
      />
      </div>

      {renderViewModal()}

      <Modal
        className="modal-overlay"
        width={calculateModalWidth('400px')}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
      >
        <div style={{ 
          padding: "var(--spacing-2xl)",
          background: 'var(--dark-bg-card)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center'
        }}>
          <div style={{ 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '3rem',
            color: 'var(--error-red)'
          }}>
            <DeleteOutlined />
          </div>
          <Title level={4} style={{ 
            color: 'var(--text-white)', 
            marginBottom: 'var(--spacing-md)',
            fontFamily: 'Poppins-Bold'
          }}>
            Confirm Delete
          </Title>
          <p style={{ 
            color: 'var(--text-gray)', 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Are you sure you want to delete this contact? This action cannot be undone.
          </p>
          <div style={{ 
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              onClick={() => setDeleteModalVisible(false)}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmDelete}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--error-red)',
                borderColor: 'var(--error-red)'
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContactUs;